import { useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Button from "react-bootstrap/Button"

import useT from "utils/hooks/useT"
import ESignatureModal from "components/Lims/ESignatureModal"
import { canSign } from "state_management/lims/segregationOfDuties"
import {
  limsSpecVersions,
  limsWorklistTemplates,
  limsSstTemplates,
  limsForskrifter,
  limsChangeControl,
  message,
} from "state_management"
import { KINDS } from "state_management/lims/limsChangeControl"

// The e-signature dialog titles per family and step — the same keys the old per-register
// approve buttons used, so the dialog reads exactly as before, just from one place.
const TITLE_KEYS = {
  [KINDS.SPEC]: { reviewer: "limsSpecVersionReviewerApproveTitle", supervisor: "limsSpecVersionSupervisorApproveTitle" },
  [KINDS.WORKLIST]: {
    reviewer: "limsWorklistTemplateReviewerApproveTitle",
    supervisor: "limsWorklistTemplateSupervisorApproveTitle",
  },
  [KINDS.SST]: { reviewer: "limsSstTemplateReviewerApproveTitle", supervisor: "limsSstTemplateSupervisorApproveTitle" },
  [KINDS.FORSKRIFT]: { reviewer: "limsForskriftReviewerApproveTitle", supervisor: "limsForskriftSupervisorApproveTitle" },
}

const SUCCESS_KEYS = {
  [KINDS.SPEC]: { reviewer: "limsSpecVersionReviewerApproved", supervisor: "limsSpecVersionSupervisorApproved" },
  [KINDS.WORKLIST]: { reviewer: "limsWorklistTemplateReviewerApproved", supervisor: "limsWorklistTemplateSupervisorApproved" },
  [KINDS.SST]: { reviewer: "limsSstTemplateReviewerApproved", supervisor: "limsSstTemplateSupervisorApproved" },
  [KINDS.FORSKRIFT]: { reviewer: "limsForskriftReviewerApproved", supervisor: "limsForskriftSupervisorApproved" },
}

// Dispatch the family's OWN approve thunk for this item and step — the approve routes stay
// the authority (capability, four-eyes, password re-auth, audit + signature server-side).
function approveThunk(item, signature) {
  const supervisor = item.requiredCapability === "supervisor"
  switch (item.kind) {
    case KINDS.SPEC:
      return supervisor
        ? limsSpecVersions.supervisorApprove(item.id, signature)
        : limsSpecVersions.reviewerApprove(item.id, signature)
    case KINDS.WORKLIST:
      return supervisor
        ? limsWorklistTemplates.supervisorApprove(item.id, signature)
        : limsWorklistTemplates.reviewerApprove(item.id, signature)
    case KINDS.SST:
      return supervisor
        ? limsSstTemplates.supervisorApprove(item.id, signature)
        : limsSstTemplates.reviewerApprove(item.id, signature)
    case KINDS.FORSKRIFT:
      return supervisor
        ? limsForskrifter.supervisorApprove(item.parentId, item.id, signature)
        : limsForskrifter.reviewerApprove(item.parentId, item.id, signature)
    default:
      throw new Error(`Unknown change-control kind: ${item.kind}`)
  }
}

// After a supervisor approval the family's prior effective version was superseded server-side;
// re-fetch that family so its register reflects it, and always re-fetch the queue itself.
function refreshThunks(item) {
  const thunks = [limsChangeControl.fetch()]
  if (item.kind === KINDS.WORKLIST) thunks.push(limsWorklistTemplates.fetch())
  if (item.kind === KINDS.SST) thunks.push(limsSstTemplates.fetch())
  if (item.kind === KINDS.FORSKRIFT) thunks.push(limsForskrifter.fetch())
  if (item.kind === KINDS.SPEC && item.analysisDefinitionId) thunks.push(limsSpecVersions.fetch(item.analysisDefinitionId))
  return thunks
}

// The ONE approve control for master data (SPEC-058 B). Active for the qualified signer, ghosted
// with the reason for everyone else (SPEC-022 doctrine: everyone can SEE, only the qualified can
// ACT). `item.actionableByMe` is the server's verdict; the reason text comes from the same
// canSign() rule every other signing button uses, so the wording matches across the app.
export default function ApprovalActions({ item }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [signing, setSigning] = useState(false)
  const [saving, setSaving] = useState(false)

  const step = item.requiredCapability // "reviewer" | "supervisor"
  const excludedMemberIds = [item.createdBy, step === "supervisor" ? item.reviewerSignerId : null].filter(Boolean)
  const gate = useSelector((state) => canSign(state, { capability: step, excludedMemberIds }), shallowEqual)
  const allowed = !!item.actionableByMe
  const reasonKey = gate.reasonKey || "limsEsigBlockedCapability"
  const label = t(step === "supervisor" ? "limsForskriftSupervisorApprove" : "limsForskriftReviewerApprove")

  const handleSign = (signature) => {
    setSaving(true)
    return dispatch(approveThunk(item, signature))
      .then(() => {
        setSigning(false)
        const successKey = SUCCESS_KEYS[item.kind][step]
        const params = item.kind === KINDS.SPEC ? { n: item.versionNumber } : { name: `${item.name} v${item.versionNumber}` }
        dispatch(message.success(t(successKey, params)))
        refreshThunks(item).forEach((thunk) => dispatch(thunk))
      })
      .finally(() => setSaving(false))
  }

  return (
    <>
      {allowed ? (
        <Button
          variant={step === "supervisor" ? "success" : "primary"}
          size="sm"
          disabled={saving}
          onClick={() => setSigning(true)}
        >
          {label}
        </Button>
      ) : (
        <span className="blocked-action-wrap" title={t(reasonKey)}>
          <Button variant="secondary" size="sm" className="blocked-action-btn" disabled>
            {label}
          </Button>
        </span>
      )}
      {signing && (
        <ESignatureModal
          title={t(TITLE_KEYS[item.kind][step])}
          requiredCapability={step}
          excludedMemberIds={excludedMemberIds}
          onSign={handleSign}
          onCancel={() => setSigning(false)}
        />
      )}
    </>
  )
}
